import React, { useState } from 'react';
import { useCustomActions } from '../hooks';
import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * CustomActionsSelector Component
 *
 * Multi-select component for selecting custom actions to assign to AI Agents.
 * Displays available custom actions with search functionality and allows selection.
 *
 * @example
 * <CustomActionsSelector
 *   selectedActionIds={formData.customActionIds}
 *   onSelectionChange={(ids) => setFormData({ ...formData, customActionIds: ids })}
 * />
 */

interface CustomActionsSelectorProps {
  /** Array of currently selected custom action IDs */
  selectedActionIds: string[];

  /** Callback fired when selection changes */
  onSelectionChange: (actionIds: string[]) => void;

  /** Optional label for the selector */
  label?: string;

  /** Optional description text */
  description?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;
}

export const CustomActionsSelector: React.FC<CustomActionsSelectorProps> = ({
  selectedActionIds = [],
  onSelectionChange,
  label = 'Ferramentas',
  description,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Fetch all active custom actions
  const { data, isLoading } = useCustomActions({ status: 'active' });

  const actions = data?.data || [];

  // Filter actions based on search term
  const filteredActions = actions.filter((action) =>
    action.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (action.description && action.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get selected actions for display
  const selectedActions = actions.filter((action) =>
    selectedActionIds.includes(action.id)
  );

  const handleToggleAction = (actionId: string) => {
    if (disabled) return;

    if (selectedActionIds.includes(actionId)) {
      // Remove from selection
      onSelectionChange(selectedActionIds.filter((id) => id !== actionId));
    } else {
      // Add to selection
      onSelectionChange([...selectedActionIds, actionId]);
    }
  };

  const handleRemoveAction = (actionId: string) => {
    if (disabled) return;
    onSelectionChange(selectedActionIds.filter((id) => id !== actionId));
  };

  return (
    <div className="space-y-3">
      {/* Label and Description */}
      {(label || description) && (
        <div>
          {label && (
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
              {label}
            </label>
          )}
          {description && (
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {description}
            </p>
          )}
        </div>
      )}

      {/* Selected Actions Display */}
      {selectedActions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedActions.map((action) => (
            <div
              key={action.id}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
              }}
            >
              <span>{action.displayName}</span>
              {!disabled && (
                <button
                  onClick={() => handleRemoveAction(action.id)}
                  className="p-0.5 rounded hover:bg-opacity-10 transition-colors"
                  style={{ color: 'var(--theme-muted-text)' }}
                  type="button"
                  aria-label={`Remover ${action.displayName}`}
                  data-testid={`tools-selector-remove-${action.id}`}
                >
                  <Icon name="x" className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selector Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-testid="tools-selector-trigger"
        className="h-[38px] w-full px-4 py-2 rounded-lg border text-left text-sm flex items-center justify-between transition-colors"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          borderColor: isOpen ? 'var(--theme-input-focus)' : 'var(--theme-input-border)',
          color: 'var(--theme-input-text)',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span>
          {selectedActions.length > 0
            ? `${selectedActions.length} ferramenta${selectedActions.length > 1 ? 's' : ''} selecionada${selectedActions.length > 1 ? 's' : ''}`
            : 'Selecionar ferramentas...'}
        </span>
        <Icon name="search" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <div
          className="border rounded-lg shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          {/* Search Input */}
          <div
            className="p-3 border-b"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <Input
              type="text"
              icon="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar ferramentas…"
              autoFocus
              data-testid="tools-selector-search"
            />
          </div>

          {/* Actions List */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading && (
              <div className="p-4 text-center">
                <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  Carregando ferramentas...
                </p>
              </div>
            )}

            {!isLoading && filteredActions.length === 0 && (
              <div className="p-4 text-center">
                <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                  {searchTerm ? 'Nenhuma ferramenta encontrada para sua busca' : 'Nenhuma ferramenta ativa disponível'}
                </p>
              </div>
            )}

            {!isLoading && filteredActions.map((action) => {
              const isSelected = selectedActionIds.includes(action.id);

              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => handleToggleAction(action.id)}
                  data-testid={`tools-selector-item-${action.id}`}
                  className="w-full p-3 text-left border-b hover:bg-opacity-5 transition-colors"
                  style={{
                    borderColor: 'var(--theme-card-border)',
                    backgroundColor: isSelected ? 'var(--theme-primary-background)' : 'transparent',
                  }}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                      style={{
                        borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
                        backgroundColor: isSelected ? 'var(--theme-primary)' : 'transparent',
                      }}
                    >
                      {isSelected && <Icon name="check" className="w-3 h-3 text-white" />}
                    </div>

                    {/* Action Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
                          {action.displayName}
                        </p>
                        <span
                          className="px-2 py-0.5 rounded text-xs"
                          style={{
                            backgroundColor: 'var(--theme-muted-text)',
                            color: 'white',
                            opacity: 0.7,
                          }}
                        >
                          {action.endpoint.method}
                        </span>
                      </div>
                      {action.description && (
                        <p className="text-xs line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
                          {action.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          {!isLoading && filteredActions.length > 0 && (
            <div
              className="p-3 border-t flex items-center justify-between"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {selectedActions.length} de {actions.length} selecionada(s)
              </p>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-xs font-medium px-3 py-1 rounded hover:bg-opacity-10 transition-colors"
                style={{ color: 'var(--theme-primary)' }}
                data-testid="tools-selector-done"
              >
                Concluído
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
